import { useEffect, useState } from 'react'
import { createDictionaryEntry, errorMessage, listDictionary } from '../../../services/admin'
import { splitWords } from '../../../lib/wordNotes'
import { dictionaryRefusal, headwordForToken, withLink } from '../dictionary'

// ─── The word picker (shared by CreateForm, EditLessonForm and DraftReviewForm) ─

/**
 * One cell per word of a sentence's *current* textEn (V2-p): a linked word
 * shows its entry's headword as a chip with an ✕; an unlinked word offers
 * «پیوند», which opens a small search over the dictionary — pick a result to
 * link it, or «کلمهٔ تازه» to create a draft entry for the word on the spot
 * and link that. A word's position in `textEn.split(/\s+/)` is its `index`
 * on the wire, the same rule as the notes.
 *
 * `links` is `{ index, entryId, headword }[]`; the caller prunes it when
 * textEn changes (`pruneWordLinks`) and sends `linksToWire(links)`.
 */
export function WordLinkInputs({ textEn, links, onChange }) {
  const [openIndex, setOpenIndex] = useState(null)
  if (!textEn.trim()) return null
  const words = splitWords(textEn)
  const linkFor = index => links.find(link => link.index === index)

  return (
    <div className="flex flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2" data-word-links>
      {words.map((word, index) => {
        const link = linkFor(index)
        return (
          <div key={index} className="flex flex-col gap-0.5">
            <span className="ltr truncate px-0.5 text-[10px] text-slate-400" title={word}>{word || '·'}</span>
            {link ? (
              <span className="flex items-center gap-1 rounded border border-brand-200 bg-brand-50 px-1.5 py-0.5 text-xs text-brand-800" data-linked={index}>
                <span dir="ltr">{link.headword}</span>
                <button type="button" title="برداشتن پیوند" aria-label={`برداشتن پیوند ${link.headword}`}
                  onClick={() => onChange(withLink(links, index, null))}
                  className="text-brand-400 hover:text-red-600">✕</button>
              </span>
            ) : openIndex === index ? (
              <WordPicker
                token={word}
                onPick={entry => { onChange(withLink(links, index, entry)); setOpenIndex(null) }}
                onClose={() => setOpenIndex(null)}
              />
            ) : (
              <button type="button" disabled={!word}
                onClick={() => setOpenIndex(index)}
                className="rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-xs text-slate-400 hover:border-brand-400 hover:text-brand-700 disabled:opacity-30">
                پیوند
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** The search box under one word: results as the admin types, plus «کلمهٔ تازه». */
export function WordPicker({ token, onPick, onClose }) {
  const [q, setQ] = useState(headwordForToken(token))
  const [results, setResults] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const term = q.trim()
    if (!term) { setResults([]); return undefined }
    let cancelled = false
    const id = setTimeout(async () => {
      try {
        const { items } = await listDictionary({ q: term, limit: 8 })
        if (!cancelled) setResults(items)
      } catch (ex) {
        if (!cancelled) setErr(errorMessage(ex))
      }
    }, 250)
    return () => { cancelled = true; clearTimeout(id) }
  }, [q])

  async function createNew() {
    const headword = headwordForToken(token)
    if (!headword) { setErr('این کلمه حرف یا رقمی ندارد.'); return }
    setBusy(true); setErr('')
    try {
      onPick(await createDictionaryEntry({ headword }))
    } catch (ex) {
      setErr(dictionaryRefusal(ex) ?? errorMessage(ex))
    } finally { setBusy(false) }
  }

  const exact = (results ?? []).find(entry => entry.normalized === headwordForToken(token))

  return (
    <div className="w-52 space-y-1 rounded-lg border border-brand-200 bg-white p-1.5 shadow-sm" data-word-picker>
      <div className="flex items-center gap-1">
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} dir="ltr" placeholder="جست‌وجوی واژه‌نامه…"
          className="w-full rounded border border-slate-200 px-1.5 py-1 text-xs focus:border-brand-500 focus:outline-none" />
        <button type="button" onClick={onClose} aria-label="بستن" className="text-slate-400 hover:text-slate-600">✕</button>
      </div>
      {results === null && q.trim() && <p className="px-1 text-[10px] text-slate-400">در حال جست‌وجو…</p>}
      {results && results.length > 0 && (
        <ul className="max-h-40 space-y-0.5 overflow-y-auto">
          {results.map(entry => (
            <li key={entry.id}>
              <button type="button" onClick={() => onPick(entry)}
                className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-start text-xs hover:bg-brand-50">
                <span dir="ltr" className="font-medium text-slate-800">{entry.headword}</span>
                <span className="truncate text-[10px] text-slate-400">{entry.meaningFa ?? entry.translationFa ?? ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {results && results.length === 0 && q.trim() && <p className="px-1 text-[10px] text-slate-400">چیزی پیدا نشد.</p>}
      {!exact && (
        <button type="button" disabled={busy} onClick={createNew}
          className="w-full rounded bg-brand-600 px-1.5 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? '…' : `کلمهٔ تازه «${headwordForToken(token)}»`}
        </button>
      )}
      {err && <p className="px-1 text-[10px] text-red-600">{err}</p>}
    </div>
  )
}
